import { useCallback, useRef, useState } from 'react';
import { wsManager } from '@/common-utilities/websocket-manager';

interface WebSocketMessage {
  event_type: string;
  text?: string;
  chatId?: string;
  requestId?: string;
  responseType?: string;
  sources?: string[];
  [key: string]: unknown;
}

interface ChatConnection {
  chatId: string;
  isConnected: boolean;
  error: string | null;
}

interface UseMultiWebSocketReturn {
  connections: Map<string, ChatConnection>;
  connect: (chatId: string) => Promise<void>;
  disconnect: (chatId: string) => void;
  sendMessage: (chatId: string, message: Record<string, unknown>) => void;
  onMessage: (handler: (data: WebSocketMessage) => void) => void;
}

export const useMultiWebSocket = (): UseMultiWebSocketReturn => {
  const [connections, setConnections] = useState<Map<string, ChatConnection>>(new Map());
  const messageHandlersRef = useRef<Set<(data: WebSocketMessage) => void>>(new Set());
  const chatHandlersRef = useRef<Map<string, (data: unknown) => void>>(new Map());

  const onMessage = useCallback((handler: (data: WebSocketMessage) => void) => {
    messageHandlersRef.current.add(handler);
    return () => {
      messageHandlersRef.current.delete(handler);
    };
  }, []);

  const connect = useCallback(async (chatId: string) => {
    console.log('[useMultiWebSocket] connect() called for chatId:', chatId);
    
    const chatHandler = (data: unknown) => {
      messageHandlersRef.current.forEach((handler) => handler(data as WebSocketMessage));
    };
    
    const errorHandler = (error: string) => {
      setConnections((prev) => {
        const next = new Map(prev);
        next.set(chatId, { chatId, isConnected: false, error });
        return next;
      });
    };

    chatHandlersRef.current.set(chatId, chatHandler);
    
    try {
      const ws = await wsManager.connect(chatId, chatHandler, errorHandler);
      
      const updateConnectionState = () => {
        setConnections((prev) => {
          const next = new Map(prev);
          next.set(chatId, { chatId, isConnected: true, error: null });
          return next;
        });
      };
      
      if (ws.readyState === WebSocket.OPEN) {
        updateConnectionState();
      } else {
        ws.addEventListener('open', updateConnectionState, { once: true });
      }
    } catch (error) {
      setConnections((prev) => {
        const next = new Map(prev);
        next.set(chatId, { chatId, isConnected: false, error: 'Connection failed' });
        return next;
      });
    }
  }, []);

  const disconnect = useCallback((chatId: string) => {
    console.log('[useMultiWebSocket] disconnect() called for chatId:', chatId);
    const handler = chatHandlersRef.current.get(chatId);
    if (handler) {
      wsManager.disconnect(chatId, handler);
      chatHandlersRef.current.delete(chatId);
    }
    setConnections((prev) => {
      const next = new Map(prev);
      next.delete(chatId);
      return next;
    });
  }, []);

  const sendMessage = useCallback((chatId: string, message: Record<string, unknown>) => {
    wsManager.send(chatId, message);
  }, []);

  return {
    connections,
    connect,
    disconnect,
    sendMessage,
    onMessage,
  };
};
